// ProjectGallery.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const ProjectGallery = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("all");

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch("https://stechbuilders.com/backend/api/projects/");
        if (!response.ok) {
          throw new Error("Failed to fetch projects");
        }
        const data = await response.json();
        setProjects(data);
        setFilteredProjects(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    // Filter projects based on type (residential/commercial/renovation)
    if (filter === "all") {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter(project => project.type.toLowerCase() === filter);
      setFilteredProjects(filtered);
    }
  }, [filter, projects]);

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } },
  };

  const imageRotate = {
    initial: { rotate: -5, scale: 1.1 },
    animate: { rotate: 0, scale: 1 },
    hover: { rotate: 2, scale: 1.05 },
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#040404] to-[#1a1a1a]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-[#7bbf42] border-t-[#f9b414] rounded-full"
        ></motion.div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#040404] to-[#1a1a1a] text-red-500">
        Error: {error}
      </div>
    );

  return (
    <section className="relative bg-gradient-to-br from-[#1a1a1a] to-[#040404] py-16 md:py-24 overflow-hidden">
      {/* Animated background elements */}
      <motion.div
        initial={{ x: -100, y: -100 }}
        animate={{
          x: [0, 100, 0],
          y: [0, 100, 0],
          rotate: [0, 180, 360],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        className="absolute top-20 right-0 w-64 h-64 rounded-full bg-[#7bbf42]/10 blur-3xl"
      ></motion.div>

      <motion.div
        initial={{ x: 100, y: 100 }}
        animate={{
          x: [0, -100, 0],
          y: [0, -100, 0],
          rotate: [0, -180, -360],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute bottom-20 left-0 w-64 h-64 rounded-full bg-[#f9b414]/10 blur-3xl"
      ></motion.div>

      {/* Animated top border */}
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: "100%" }}
        transition={{ duration: 1.5, delay: 0.3 }}
        className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#7bbf42] via-[#f9b414] to-[#7bbf42]"
      ></motion.div>

      <div className="container pt-10 mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center mb-16"
        >
          <motion.span
            whileHover={{ scale: 1.05 }}
            className="inline-block px-4 py-2 bg-[#7bbf42]/20 text-[#7bbf42] rounded-full mb-4 font-medium"
          >
            Our Portfolio
          </motion.span>
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            <motion.span
              animate={{ color: ["#ffffff", "#f9b414", "#ffffff"] }}
              transition={{ duration: 8, repeat: Infinity }}
              className="inline-block mr-2"
            >
              Featured
            </motion.span>
            Projects
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.6, type: "spring" }}
            className="w-20 h-1 bg-[#f9b414] mx-auto mb-6"
          ></motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="text-gray-300 max-w-3xl mx-auto text-lg"
          >
            Showcasing our finest construction projects across different sectors and locations
          </motion.p>
        </motion.div>

        {/* Filter Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          {["all", "residential", "commercial", "renovation"].map((category) => (
            <motion.button
              key={category}
              whileHover={{
                scale: 1.05,
                backgroundColor: filter === category ? "#f9b414" : "#7bbf42",
                color: "#040404",
              }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setFilter(category)}
              className={`px-4 py-2 rounded-full capitalize font-medium transition-all ${
                filter === category
                  ? "bg-[#f9b414] text-[#040404] shadow-lg"
                  : "bg-[#040404] text-white border border-[#7bbf42]/40 hover:border-[#f9b414]"
              }`}
            >
              {category}
            </motion.button>
          ))}
        </motion.div>

        {/* Projects Grid */}
        {filteredProjects.length > 0 ? (
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredProjects.map((project) => (
              <motion.div
                key={project.id}
                variants={item}
                whileHover={{ y: -15 }}
                className="relative group overflow-hidden rounded-2xl shadow-2xl"
                layout
              >
                <motion.div
                  variants={imageRotate}
                  initial="initial"
                  animate="animate"
                  whileHover="hover"
                  transition={{ type: "spring", stiffness: 200 }}
                  className="h-72 w-full overflow-hidden"
                >
                  <img
                    src={project.mainImage}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#040404]/90 via-[#040404]/30 to-transparent"></div>
                </motion.div>

                <motion.div
                  initial={{ y: 100 }}
                  whileHover={{ y: 0 }}
                  transition={{ type: "spring", stiffness: 100 }}
                  className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#040404] to-transparent"
                >
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="inline-block px-3 py-1 mb-2 bg-[#f9b414] text-[#040404] text-sm font-bold rounded-full"
                  >
                    {project.type}
                  </motion.div>
                  <motion.h3
                    whileHover={{ color: "#f9b414" }}
                    className="text-xl font-bold text-white mb-2"
                  >
                    {project.title}
                  </motion.h3>
                  <motion.p className="text-gray-300 text-sm mb-4 line-clamp-2">
                    {project.description.substring(0, 100)}...
                  </motion.p>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <button
                      onClick={() => setSelectedProject(project)}
                      className="w-full py-2 bg-[#7bbf42] hover:bg-[#f9b414] text-[#040404] font-bold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-[#7bbf42]/30"
                    >
                      View Details
                    </button>
                  </motion.div>
                </motion.div>

                {/* Location badge */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="absolute top-4 left-4 px-3 py-1 bg-[#040404]/90 text-[#f9b414] text-xs font-bold rounded-full flex items-center"
                >
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    ></path>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    ></path>
                  </svg>
                  {project.location}
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <motion.div
              animate={{
                rotate: [0, 10, -10, 0],
                scale: [1, 1.1, 1],
              }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="inline-block mb-6"
            >
              <svg
                className="w-16 h-16 text-[#f9b414]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </motion.div>
            <h3 className="text-2xl font-bold text-[#f9b414] mb-4">
              No Projects Found
            </h3>
            <p className="text-gray-400 max-w-md mx-auto">
              We don't have any projects in this category at the moment.
            </p>
          </motion.div>
        )}

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true, margin: "-100px" }}
          className="mt-20 text-center"
        >
          <motion.h3
            whileHover={{ scale: 1.02 }}
            className="text-2xl md:text-3xl font-bold text-white mb-6"
          >
            Ready to start your project?
          </motion.h3>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block"
          >
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-[#f9b414] hover:bg-[#7bbf42] text-[#040404] font-bold rounded-lg shadow-lg hover:shadow-xl transition-all group"
            >
              <span>Get a Free Consultation</span>
              <motion.span
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="ml-2 group-hover:translate-x-1 transition-transform"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                  ></path>
                </svg>
              </motion.span>
            </Link>
          </motion.div>
        </motion.div>
      </div>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#040404]/90 backdrop-blur-sm"
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 25 }}
              className="relative max-w-5xl w-full max-h-[90vh] overflow-y-auto bg-[#1a1a1a] rounded-2xl shadow-2xl border border-[#7bbf42]/30"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 z-10 p-2 bg-[#040404] rounded-full hover:bg-[#f9b414] transition-colors"
              >
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="h-64 md:h-96 w-full overflow-hidden relative"
              >
                <motion.img
                  src={selectedProject.mainImage}
                  alt={selectedProject.title}
                  className="w-full h-full object-cover"
                  initial={{ scale: 1.2, rotate: -2 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ duration: 1 }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#040404]/90 via-[#040404]/30 to-transparent"></div>
              </motion.div>

              <div className="p-6 md:p-8">
                <div className="flex flex-wrap gap-2 mb-4">
                  <motion.span
                    whileHover={{ scale: 1.05 }}
                    className="px-3 py-1 bg-[#f9b414] text-[#040404] text-sm font-bold rounded-full"
                  >
                    {selectedProject.type}
                  </motion.span>
                  <motion.span
                    whileHover={{ scale: 1.05 }}
                    className="px-3 py-1 bg-[#7bbf42] text-[#040404] text-sm font-bold rounded-full"
                  >
                    {selectedProject.completionDate}
                  </motion.span>
                  <motion.span
                    whileHover={{ scale: 1.05 }}
                    className="px-3 py-1 bg-[#040404] text-[#f9b414] border border-[#f9b414] text-sm font-bold rounded-full flex items-center"
                  >
                    <svg
                      className="w-3 h-3 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      ></path>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      ></path>
                    </svg>
                    {selectedProject.location}
                  </motion.span>
                </div>

                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-2xl md:text-3xl font-bold text-white mb-4"
                >
                  {selectedProject.title}
                </motion.h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-[#040404] p-4 rounded-lg">
                    <p className="text-gray-400 text-sm">Client</p>
                    <p className="text-white font-medium">{selectedProject.client}</p>
                  </div>
                  <div className="bg-[#040404] p-4 rounded-lg">
                    <p className="text-gray-400 text-sm">Duration</p>
                    <p className="text-white font-medium">{selectedProject.duration}</p>
                  </div>
                  <div className="bg-[#040404] p-4 rounded-lg">
                    <p className="text-gray-400 text-sm">Size</p>
                    <p className="text-white font-medium">{selectedProject.size}</p>
                  </div>
                </div>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-gray-300 mb-6"
                >
                  {selectedProject.description}
                </motion.p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <h4 className="text-lg font-semibold text-[#f9b414] mb-3 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        ></path>
                      </svg>
                      Project Features
                    </h4>
                    <ul className="space-y-3">
                      {selectedProject.features.map((feature, i) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.5 + i * 0.1 }}
                          className="flex items-start"
                        >
                          <svg
                            className="w-4 h-4 text-[#7bbf42] mt-1 mr-2 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                            ></path>
                          </svg>
                          <span className="text-gray-300">{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <h4 className="text-lg font-semibold text-[#f9b414] mb-3 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        ></path>
                      </svg>
                      Project Tags
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.tags.map((tag, i) => (
                        <motion.span
                          key={i}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.5 + i * 0.05 }}
                          className="px-3 py-1 bg-[#040404] text-[#7bbf42] border border-[#7bbf42]/30 rounded-full text-sm"
                        >
                          {tag}
                        </motion.span>
                      ))}
                    </div>
                  </motion.div>
                </div>

                {/* Gallery with scroll animation */}
                {selectedProject.gallery && selectedProject.gallery.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="mb-8"
                  >
                    <h4 className="text-lg font-semibold text-[#f9b414] mb-4 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        ></path>
                      </svg>
                      Project Gallery
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {selectedProject.gallery.map((image, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true, margin: "-50px" }}
                          transition={{ delay: i * 0.1, type: "spring" }}
                          whileHover={{ scale: 1.05, zIndex: 10 }}
                          className="overflow-hidden rounded-xl cursor-pointer relative group"
                        >
                          <img
                            src={image}
                            alt={`${selectedProject.title} - ${i + 1}`}
                            className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-[#040404]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                            <motion.span
                              whileHover={{ scale: 1.2 }}
                              className="text-white bg-[#f9b414] p-2 rounded-full"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                ></path>
                              </svg>
                            </motion.span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                  className="flex flex-col sm:flex-row gap-4"
                >
                  <Link
                    to="/contact"
                    className="flex-1 px-6 py-3 bg-[#f9b414] hover:bg-[#7bbf42] text-[#040404] font-bold rounded-lg text-center transition-all hover:shadow-lg hover:shadow-[#f9b414]/30 flex items-center justify-center"
                  >
                    <span>Start Your Project</span>
                    <svg
                      className="w-5 h-5 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      ></path>
                    </svg>
                  </Link>
                  <button
                    onClick={() => setSelectedProject(null)}
                    className="flex-1 px-6 py-3 border border-[#f9b414] text-[#f9b414] hover:bg-[#f9b414]/10 font-bold rounded-lg transition-colors flex items-center justify-center"
                  >
                    <span>Close Preview</span>
                    <svg
                      className="w-5 h-5 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ProjectGallery;